import { useState } from "react"
import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import PhotoLightbox from "@/components/PhotoLightbox"
import { HeroFilmSlides } from "@/components/gisela/HeroBand"
import { bandBg } from "@/components/gisela/TextBand"
import { useLocalePath } from "@/lib/locale-path"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps } from "@/types/inertia"
import { BrandMotif } from "@/components/ui/brand-motif"

interface Photo {
  id: number
  url: string
  full_url: string
  alt_text: string
}

interface Location {
  id: number
  name: string
  /** That location's own gallery, where the rooms are named one by one. */
  gallery_href: string
  photos: Photo[]
}

interface HeroPhoto {
  id: number
  url: string | null
  alt_text: string | null
}

interface Props {
  locations: Location[]
  hero?: { slides: HeroPhoto[]; interval_seconds: number; film?: string | null }
  content?: { hero: { eyebrow?: string; heading?: string; subheading?: string; _hidden?: boolean } }
  meta?: PageMeta
}

// Every location's rooms in one place — the browsing page, for a visitor who
// hasn't chosen between Prestwich and Hebden Bridge yet. Each group heads to
// that location's own gallery, which is where the photos are broken out room
// by room.
export default function Gallery({
  locations,
  hero = { slides: [], interval_seconds: 6 },
  content = { hero: {} },
  meta
}: Props) {
  const { translations } = usePage<PageProps>().props
  const t = translations.gallery
  const tg = translations.sanctuary_pages.gallery
  const tl = translations.sanctuary_pages.location
  const lp = useLocalePath()
  const heroSlides = hero.slides.filter((s): s is HeroPhoto & { url: string } => !!s.url)

  // One list across every location, so the lightbox arrows carry on past the
  // end of a location rather than stopping there.
  const allPhotos = locations.flatMap((l) => l.photos)
  const [lightbox, setLightbox] = useState<number | null>(null)

  const title = meta?.title ?? "Gallery — Sanctuary Therapy Centre"
  const description =
    meta?.description ??
    "Photos of the therapy rooms at Sanctuary Therapy Centre in Prestwich and Hebden Bridge — the rooms, the buildings and the shared spaces."

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={
            meta?.description ??
            "Browse photos of our calm, thoughtfully furnished therapy rooms in Prestwich and Hebden Bridge."
          }
        />
      </Head>
      <PublicLayout>
        {/* The page's hero: the admin's copy over their photos, or the
            plain blue band when none are placed. */}
        {!sectionHidden(content.hero) && (
          <section className="relative overflow-hidden bg-teal pt-32 pb-16 text-white">
            <HeroFilmSlides slides={heroSlides} intervalMs={hero.interval_seconds * 1000} film={hero.film} />
            <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
            <div className="relative mx-auto max-w-4xl px-6 text-center lg:px-8">
              <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
                {content.hero.eyebrow || t.eyebrow}
              </p>
              <h1 className="text-white">{content.hero.heading || t.title}</h1>
              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75">
                {content.hero.subheading || t.subtitle}
              </p>
            </div>
          </section>
        )}

        {locations.length > 0 ? (
          locations.map((location, index) => {
            const offset = allPhotos.findIndex((p) => p.id === location.photos[0]?.id)
            return (
              // The locations alternate cream/stone like every other banded
              // page, so a long gallery still reads as separate places.
              <section key={location.id} className={`${bandBg(index)} py-14`}>
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
                    <h2>{location.name}</h2>
                    <a
                      href={lp(location.gallery_href)}
                      className="text-sm font-medium text-teal hover:underline"
                    >
                      {tl.all_photos.replace("%{name}", location.name)}
                    </a>
                  </div>
                  <p className="mt-2 text-sm text-ink-muted">
                    {(location.photos.length === 1 ? tg.photo_count_one : tg.photo_count_other).replace(
                      "%{count}",
                      String(location.photos.length)
                    )}
                  </p>
                  <div className="mt-6 columns-2 gap-4 sm:columns-3 lg:columns-4">
                    {location.photos.map((photo, i) => (
                      <div
                        key={photo.id}
                        className="group mb-4 cursor-zoom-in overflow-hidden rounded-xl break-inside-avoid"
                        onClick={() => setLightbox(offset + i)}
                      >
                        <img
                          src={photo.url}
                          alt={photo.alt_text}
                          className="w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )
          })
        ) : (
          <section className="bg-cream py-14">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <div className="py-24 text-center">
                <p className="text-ink-muted">{t.coming_soon}</p>
                <p className="mt-2 text-sm text-ink-muted">
                  {tg.add_photos_hint}{" "}
                  <a href="/admin/sites" className="text-teal-mid hover:text-teal">
                    {tg.admin_portal}
                  </a>
                  .
                </p>
              </div>
            </div>
          </section>
        )}
      </PublicLayout>

      <PhotoLightbox photos={allPhotos} index={lightbox} onChange={setLightbox} />
    </>
  )
}
